import React, { useState } from "react";
import { User } from "@supabase/supabase-js";
import Logo from "./Logo";
import AuthModal from "./AuthModal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { useRetractableBar } from "@/hooks/useRetractableBar";

interface GlobalHeaderProps {
  user: User | null;
  profile: { avatar_url: string | null; avatar_url_200: string | null; avatar_url_80: string | null; display_name: string | null } | null;
  onLogout: () => void;
  shouldRetract?: boolean;
}

const GlobalHeader: React.FC<GlobalHeaderProps> = ({ user, profile, onLogout, shouldRetract = false }) => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const { isVisible, hasMounted } = useRetractableBar({ shouldRetract, edge: "top" });

  const getInitials = () => {
    if (profile?.display_name) {
      return profile.display_name.substring(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return "U";
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[60] bg-background shadow-elegant border-b border-border/50 
        transition-all duration-1000 ease-in-out 
        ${hasMounted ? (isVisible ? "translate-y-0" : "-translate-y-[calc(100%-8px)]") : "-translate-y-full"}
        ${!hasMounted ? "animate-slide-in-down" : ""}`}
    >
      <div className="flex items-center justify-between px-6 py-2">
        <div className="flex items-center gap-3">
          <Logo size={40} />
          <h1 className="text-xl font-semibold text-foreground">Carline Consensor</h1>
        </div>

        <div className="flex items-center gap-4">
          {!user && (
            <button
              onClick={() => setShowAuthModal(true)}
              className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 rounded-md transition-colors"
            >
              <span className="md:hidden">Sign In</span>
              <span className="hidden md:inline">Sign In / Sign Up</span>
            </button>
          )}

          {user && (
            <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
              <AlertDialogTrigger asChild>
                <button className="focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-full">
                  <Avatar className="h-10 w-10 cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all">
                    <AvatarImage 
                      src={profile?.avatar_url_80 || profile?.avatar_url_200 || profile?.avatar_url || undefined} 
                      alt={profile?.display_name || "User"} 
                    />
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                      {getInitials()}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Log Out</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to log out?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={onLogout}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Log Out
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </header>
  );
};

export default GlobalHeader;
